import React from "react";
import {
  Flame,
  Droplets,
  Trees,
  Sparkles,
  BookOpen,
  Sun,
  Crown,
} from "lucide-react";
import { BABASAHEB_IMG } from "./BabasahebPortrait";
import DharmaWheel from "./DharmaWheel";

const BUDDHA_PURNIMA_IMG =
  "/images/1hv4vej6-34a5f882-4e27-4725-98d6-99cfc887ac1a.png";

/**
 * Per-event cover art. Returns a JSX node for the visual.
 * Routes by keyword in the event title to the most relevant visual.
 */
export default function EventCover({ title }) {
  const t = (title || "").toLowerCase();

  if (t.includes("bhima koregaon")) {
    return (
      <Backdrop from="#0A2540" to="#001026" accent="#CFA052">
        <PillarMotif />
        <BadgeText>Shaurya Din</BadgeText>
      </Backdrop>
    );
  }
  if (t.includes("mahad") || t.includes("satyagraha")) {
    return (
      <Backdrop from="#1F4068" to="#001B3A" accent="#E07A5F">
        <Droplets size={88} className="absolute inset-0 m-auto text-saffron/80" strokeWidth={1.2} />
        <RippleMotif />
        <BadgeText>Satyagraha</BadgeText>
      </Backdrop>
    );
  }
  if (t.includes("ambedkar jayanti")) {
    return (
      <PortraitCover img={BABASAHEB_IMG} eyebrow="14 April · Jayanti" tone="celebrate" />
    );
  }
  if (t.includes("buddha purnima")) {
    return (
      <div className="relative w-full h-full overflow-hidden bg-navy">
        <img
          src={BUDDHA_PURNIMA_IMG}
          alt="Buddha Purnima"
          className="w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent" />
        <div className="absolute bottom-3 left-3">
          <span className="text-[10px] uppercase tracking-[0.2em] text-gold">Flagship Event</span>
        </div>
      </div>
    );
  }
  if (t.includes("family camping") || t.includes("camping")) {
    return (
      <Backdrop from="#1B4332" to="#0A2540" accent="#CFA052">
        <Trees size={88} className="absolute inset-0 m-auto text-pearl/85" strokeWidth={1.2} />
        <BadgeText>Family Retreat</BadgeText>
      </Backdrop>
    );
  }
  if (t.includes("dhamma chakra")) {
    return (
      <Backdrop from="#001B3A" to="#0A2540" accent="#CFA052">
        <div className="absolute inset-0 flex items-center justify-center opacity-80">
          <DharmaWheel size={140} color="#CFA052" ringColor="#CFA052" />
        </div>
        <BadgeText>14 October</BadgeText>
      </Backdrop>
    );
  }
  if (t.includes("mahaparinirvan")) {
    return (
      <div className="relative w-full h-full overflow-hidden bg-black">
        <img
          src={BABASAHEB_IMG}
          alt="Babasaheb Mahaparinirvan Din"
          style={{ objectPosition: "50% 28%", filter: "grayscale(1) contrast(1.05) brightness(0.85)" }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-black/20" />
        <Flame size={42} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-gold drop-shadow-lg" strokeWidth={1.4} />
        <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.22em] text-gold">
          6 December · Remembrance
        </span>
      </div>
    );
  }
  if (t.includes("manusmriti")) {
    return (
      <Backdrop from="#5B1A1A" to="#001026" accent="#E07A5F">
        <div className="absolute inset-0 flex items-center justify-center">
          <BookOpen size={84} className="text-saffron/90" strokeWidth={1.2} />
          <Flame size={36} className="absolute -top-2 right-[34%] text-gold-light" strokeWidth={1.4} />
        </div>
        <BadgeText>25 December</BadgeText>
      </Backdrop>
    );
  }
  if (t.includes("new year") || t.includes("celebration")) {
    return (
      <Backdrop from="#001B3A" to="#0A2540" accent="#CFA052">
        <Sparkles size={88} className="absolute inset-0 m-auto text-gold" strokeWidth={1.2} />
        <ConfettiMotif />
        <BadgeText>31 December</BadgeText>
      </Backdrop>
    );
  }

  // Generic fallback
  return (
    <Backdrop from="#001B3A" to="#0A2540" accent="#CFA052">
      <Sun size={80} className="absolute inset-0 m-auto text-gold" strokeWidth={1.2} />
    </Backdrop>
  );
}

function Backdrop({ from, to, accent, children }) {
  return (
    <div
      className="relative w-full h-full overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)` }}
    >
      <div
        className="absolute -right-12 -top-12 w-48 h-48 rounded-full opacity-25 blur-2xl"
        style={{ background: accent }}
      />
      <div
        className="absolute -left-10 -bottom-10 w-36 h-36 rounded-full opacity-20 blur-2xl"
        style={{ background: accent }}
      />
      {children}
    </div>
  );
}

function BadgeText({ children }) {
  return (
    <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.22em] text-gold">
      {children}
    </span>
  );
}

function PortraitCover({ img, eyebrow, tone = "celebrate" }) {
  const overlay =
    tone === "solemn"
      ? "bg-gradient-to-t from-navy/95 via-navy/40 to-navy/30"
      : "bg-gradient-to-t from-navy/85 via-navy/20 to-transparent";
  return (
    <div className="relative w-full h-full overflow-hidden bg-navy">
      <img
        src={img}
        alt="Babasaheb Dr. B.R. Ambedkar"
        style={{ objectPosition: "50% 30%", filter: tone === "solemn" ? "grayscale(0.35)" : "none" }}
        className="w-full h-full object-cover"
      />
      <div className={`absolute inset-0 ${overlay}`} />
      <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.22em] text-gold">
        {eyebrow}
      </span>
    </div>
  );
}

function PillarMotif() {
  return (
    <svg viewBox="0 0 200 200" className="absolute inset-0 m-auto w-full h-full opacity-90" aria-hidden="true">
      <defs>
        <linearGradient id="pcol" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#CFA052" />
          <stop offset="100%" stopColor="#A88340" />
        </linearGradient>
      </defs>
      <rect x="86" y="38" width="28" height="124" fill="url(#pcol)" />
      <rect x="78" y="32" width="44" height="10" fill="#CFA052" />
      <rect x="78" y="158" width="44" height="10" fill="#CFA052" />
      <circle cx="100" cy="22" r="6" fill="#CFA052" />
    </svg>
  );
}

function RippleMotif() {
  return (
    <svg viewBox="0 0 200 200" className="absolute inset-0 m-auto w-full h-full opacity-30" aria-hidden="true">
      <circle cx="100" cy="120" r="20" stroke="#E07A5F" strokeWidth="1.5" fill="none" />
      <circle cx="100" cy="120" r="38" stroke="#E07A5F" strokeWidth="1.2" fill="none" />
      <circle cx="100" cy="120" r="58" stroke="#E07A5F" strokeWidth="1" fill="none" />
      <circle cx="100" cy="120" r="80" stroke="#E07A5F" strokeWidth="0.8" fill="none" />
    </svg>
  );
}

function ConfettiMotif() {
  const dots = [
    { x: 20, y: 30, c: "#CFA052" },
    { x: 160, y: 50, c: "#E07A5F" },
    { x: 140, y: 140, c: "#E2B873" },
    { x: 40, y: 160, c: "#CFA052" },
    { x: 80, y: 30, c: "#E07A5F" },
    { x: 110, y: 170, c: "#E2B873" },
  ];
  return (
    <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full opacity-90" aria-hidden="true">
      {dots.map((d, i) => (
        <circle key={i} cx={d.x} cy={d.y} r="3" fill={d.c} />
      ))}
    </svg>
  );
}

export function EventTypeIcon({ title, size = 18, className = "" }) {
  const t = (title || "").toLowerCase();
  let Icon = Sun;
  if (t.includes("bhima koregaon")) Icon = Crown;
  else if (t.includes("mahad")) Icon = Droplets;
  else if (t.includes("ambedkar jayanti")) Icon = Sun;
  else if (t.includes("buddha purnima")) Icon = Sparkles;
  else if (t.includes("camping")) Icon = Trees;
  else if (t.includes("dhamma chakra")) Icon = Sun;
  else if (t.includes("mahaparinirvan")) Icon = Flame;
  else if (t.includes("manusmriti")) Icon = BookOpen;
  else if (t.includes("new year")) Icon = Sparkles;
  return <Icon size={size} className={className} />;
}
